import { Transition } from '@headlessui/react';
import { type PropsWithChildren, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useScrollLock } from 'usehooks-ts';
import cn from '@/utils/merge-class-names';

type Props = {
  show: boolean;
  containerClassName?: string;
  className?: string;
  onClose: () => void;
  shouldCloseOnInteraction?: boolean;
  shouldLockScroll?: boolean;
};

export default function Flyout({
  show,
  containerClassName,
  className,
  shouldCloseOnInteraction = true,
  shouldLockScroll = true,
  onClose,
  children,
}: PropsWithChildren<Props>) {
  const { lock, unlock } = useScrollLock({ autoLock: false });

  useEffect(() => {
    if (show && shouldLockScroll) {
      lock();
    }

    return () => {
      if (shouldLockScroll) {
        unlock();
      }
    };
  }, [show, shouldLockScroll]);

  const modalContent = (
    <div
      id='flyout-modal'
      className={cn(
        'fixed inset-0 items-center z-50 hidden flex-col justify-end bg-black/40',
        {
          flex: show,
        },
        containerClassName,
      )}
      onClick={shouldCloseOnInteraction ? onClose : undefined}
      onContextMenu={(e) => e.preventDefault()}>
      <Transition
        show={show}
        enter='transform transition duration-400'
        enterFrom='translate-y-full'
        enterTo='translate-y-0'>
        <div className={cn('mx-4 pb-safe-offset-8 desktop:max-w-screen-xxs w-full', className)}>{children}</div>
      </Transition>
    </div>
  );

  return createPortal(
    modalContent,
    document.body, // This is where the portal will be attached
  );
}
